import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Warning as WarningIcon,
  BugReport as BugReportIcon,
} from "@mui/icons-material";
import { Title, useNotify } from "react-admin";
import config from "../config";
import { getValidToken } from "../services/tokenRefreshService";

/**
 * DevMenu Component
 *
 * Developer tools for database management in LOCAL development environment ONLY.
 * Provides quick access to common dev operations like deleting test data.
 *
 * CRITICAL: This component should ONLY be accessible in development mode.
 * Production/staging access is blocked at both UI and API levels.
 *
 * Environment Safety:
 * - UI renders warning if not in development mode
 * - Backend endpoints check NODE_ENV before executing
 * - All operations target local database only
 */
const DevMenu: React.FC = () => {
  const notify = useNotify();
  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [users, setUsers] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [orphanedData, setOrphanedData] = useState<{
    total: number;
    orphanedCount: number;
    orphanedProducts: any[];
  } | null>(null);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedStore, setSelectedStore] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    action: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    action: () => {},
  });

  const isDevelopment = import.meta.env.DEV;

  useEffect(() => {
    if (isDevelopment) {
      fetchUsers();
      fetchStores();
      fetchOrphanedProducts();
    }
  }, [isDevelopment]);

  /**
   * Fetch all users from database
   */
  const fetchUsers = async () => {
    try {
      const token = await getValidToken();
      if (!token) return;
      const response = await fetch(`${config.API_URL}/admin/dev/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const json = await response.json();
      setUsers(json.data || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  /**
   * Fetch all stores from database
   */
  const fetchStores = async () => {
    try {
      const token = await getValidToken();
      if (!token) return;
      const response = await fetch(`${config.API_URL}/admin/dev/stores`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const json = await response.json();
      setStores(json.data || []);
    } catch (error) {
      console.error("Failed to fetch stores:", error);
    }
  };

  /**
   * Fetch orphaned products count
   */
  const fetchOrphanedProducts = async () => {
    try {
      const token = await getValidToken();
      if (!token) return;
      const response = await fetch(
        `${config.API_URL}/admin/dev/products/orphaned`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) return;
      const json = await response.json();
      setOrphanedData(json.data || null);
    } catch (error) {
      console.error("Failed to fetch orphaned products:", error);
    }
  };

  /**
   * Generic dev operation executor
   */
  const executeDevOperation = async (
    endpoint: string,
    method: string,
    successMessage: string,
    errorMessage: string,
    loadingKey: string,
    body?: any
  ) => {
    setLoading((prev) => ({ ...prev, [loadingKey]: true }));
    const token = await getValidToken();

    try {
      const response = await fetch(`${config.API_URL}${endpoint}`, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const json = await response.json();
      notify(successMessage, { type: "success" });

      // Refresh lists after deletion
      if (loadingKey.includes("deleteUser")) {
        fetchUsers();
      } else if (loadingKey.includes("deleteProducts")) {
        fetchStores();
      } else if (loadingKey.includes("deleteOrphaned")) {
        fetchOrphanedProducts();
      }

      return json;
    } catch (error) {
      console.error(`Dev operation error (${loadingKey}):`, error);
      notify(errorMessage, { type: "error" });
    } finally {
      setLoading((prev) => ({ ...prev, [loadingKey]: false }));
      setConfirmDialog({ open: false, title: "", message: "", action: () => {} });
    }
  };

  /**
   * Delete user and all associated data
   */
  const handleDeleteUser = (userId: string, userEmail: string) => {
    setConfirmDialog({
      open: true,
      title: "Delete User",
      message: `Are you sure you want to delete user "${userEmail}"? This will permanently delete:

• User account
• All addresses
• All orders
• All cart items
• All favorite products
• All notifications

This action CANNOT be undone!`,
      action: () =>
        executeDevOperation(
          `/admin/dev/users/${userId}`,
          "DELETE",
          `User "${userEmail}" and all associated data deleted`,
          `Failed to delete user "${userEmail}"`,
          `deleteUser_${userId}`
        ),
    });
  };

  /**
   * Delete all products for a specific store
   */
  const handleDeleteStoreProducts = (storeId: string, storeName: string) => {
    setConfirmDialog({
      open: true,
      title: "Delete Store Products",
      message: `Are you sure you want to delete ALL products for store "${storeName}"?

This will delete all product_availability records for this store location.

This action CANNOT be undone!`,
      action: () =>
        executeDevOperation(
          `/admin/dev/stores/${storeId}/products`,
          "DELETE",
          `All products deleted for store "${storeName}"`,
          `Failed to delete products for store "${storeName}"`,
          `deleteProducts_${storeId}`
        ),
    });
  };

  /**
   * Delete orphaned products (products without availability records)
   */
  const handleDeleteOrphanedProducts = () => {
    setConfirmDialog({
      open: true,
      title: "Delete Orphaned Products",
      message: `Are you sure you want to delete ALL orphaned products?

Orphaned products are products without any product_availability records (usually from failed syncs).

This will delete:
• ${orphanedData?.orphanedCount || 0} orphaned product records
• Their attachments (images)
• Their Bill of Materials (BOMs)

Products with valid availability records will NOT be affected.

This action CANNOT be undone!`,
      action: () =>
        executeDevOperation(
          `/admin/dev/products/orphaned`,
          "DELETE",
          `Orphaned products deleted successfully`,
          `Failed to delete orphaned products`,
          `deleteOrphanedProducts`
        ),
    });
  };

  /**
   * Delete ALL products in database
   */
  const handleDeleteAllProducts = () => {
    setConfirmDialog({
      open: true,
      title: "Delete ALL Products",
      message: `⚠️ DANGER ZONE ⚠️

Are you sure you want to delete ALL PRODUCTS from the entire database?

This will delete:
• All product records
• All product_availability records (all stores)
• All product_attachment records
• All product BOMs

This action CANNOT be undone!

Type "DELETE ALL PRODUCTS" to confirm.`,
      action: () =>
        executeDevOperation(
          `/admin/dev/products`,
          "DELETE",
          `ALL products deleted from database`,
          `Failed to delete all products`,
          `deleteAllProducts`
        ),
    });
  };

  if (!isDevelopment) {
    return (
      <Box sx={{ padding: 3 }}>
        <Title title="Dev Menu" />
        <Alert severity="error">
          <Typography variant="h6">Access Denied</Typography>
          <Typography variant="body2">
            Dev Menu is only available in <strong>development mode</strong>.
            <br />
            Current environment: <strong>{import.meta.env.MODE || "production"}</strong>
            <br />
            <br />
            This restriction prevents accidental data deletion in
            staging/production environments.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Title title="Dev Menu" />

      <Alert severity="warning" sx={{ marginBottom: 3 }}>
        <Typography variant="body2">
          <strong>⚠️ DEVELOPMENT MODE ONLY</strong>
          <br />
          Environment: <strong>{import.meta.env.MODE}</strong>
          <br />
          API: <strong>{config.API_URL}</strong>
          <br />
          Database: <strong>LOCAL (dev database)</strong>
          <br />
          <br />
          These tools manipulate the LOCAL database only. All operations are
          destructive and CANNOT be undone.
        </Typography>
      </Alert>

      {/* Users Management */}
      <Card sx={{ marginBottom: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <BugReportIcon sx={{ marginRight: 1, color: "error.main" }} />
            <Typography variant="h6">User Management</Typography>
            <Chip
              label={`${users.length} users`}
              size="small"
              sx={{ marginLeft: 2 }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            Delete test users and all associated data (addresses, orders, cart,
            favorites, notifications).
          </Typography>

          {loading.fetchUsers ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
              <CircularProgress />
            </Box>
          ) : users.length === 0 ? (
            <Alert severity="info">No users in database</Alert>
          ) : (
            <List dense>
              {users.slice(0, 20).map((user) => (
                <ListItem key={user.id} divider>
                  <ListItemText
                    primary={user.email || user.phoneNumber || "No email/phone"}
                    secondary={`ID: ${user.id.substring(0, 8)}... | Created: ${new Date(user.createdAt).toLocaleDateString()}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() =>
                        handleDeleteUser(
                          user.id,
                          user.email || user.phoneNumber || user.id
                        )
                      }
                      disabled={loading[`deleteUser_${user.id}`]}
                    >
                      {loading[`deleteUser_${user.id}`] ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DeleteIcon color="error" />
                      )}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          {users.length > 20 && (
            <Alert severity="info" sx={{ marginTop: 2 }}>
              Showing first 20 of {users.length} users. Use database queries for
              bulk operations.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ marginY: 3 }} />

      {/* Store Products Management */}
      <Card sx={{ marginBottom: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <WarningIcon sx={{ marginRight: 1, color: "warning.main" }} />
            <Typography variant="h6">Store Products Management</Typography>
            <Chip
              label={`${stores.length} stores`}
              size="small"
              sx={{ marginLeft: 2 }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            Delete all products (product_availability records) for a specific
            store location.
          </Typography>

          {loading.fetchStores ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
              <CircularProgress />
            </Box>
          ) : stores.length === 0 ? (
            <Alert severity="info">No stores in database</Alert>
          ) : (
            <List dense>
              {stores.map((store) => (
                <ListItem key={store.id} divider>
                  <ListItemText
                    primary={store.name}
                    secondary={`External ID: ${store.externalId} | Active: ${store.isActive ? "Yes" : "No"}`}
                  />
                  <ListItemSecondaryAction>
                    <Button
                      variant="outlined"
                      color="warning"
                      size="small"
                      startIcon={
                        loading[`deleteProducts_${store.id}`] ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DeleteIcon />
                        )
                      }
                      onClick={() =>
                        handleDeleteStoreProducts(store.id, store.name)
                      }
                      disabled={loading[`deleteProducts_${store.id}`]}
                    >
                      Delete Products
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ marginY: 3 }} />

      {/* Orphaned Products Management */}
      <Card sx={{ marginBottom: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <BugReportIcon sx={{ marginRight: 1, color: "warning.main" }} />
            <Typography variant="h6">Orphaned Products</Typography>
            {orphanedData && (
              <Chip
                label={`${orphanedData.orphanedCount} orphaned of ${orphanedData.total} total`}
                size="small"
                color={orphanedData.orphanedCount > 0 ? "error" : "success"}
                sx={{ marginLeft: 2 }}
              />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            Orphaned products are products without any product_availability
            records (usually from failed syncs). These products exist in the
            database but are not available at any store.
          </Typography>

          {!orphanedData ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
              <CircularProgress />
            </Box>
          ) : orphanedData.orphanedCount === 0 ? (
            <Alert severity="success">
              ✅ No orphaned products found! All products have store
              availability.
            </Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ marginBottom: 2 }}>
                <Typography variant="body2">
                  <strong>
                    Found {orphanedData.orphanedCount} orphaned products
                  </strong>
                  <br />
                  These products were likely created during a sync but never got
                  availability records.
                </Typography>
              </Alert>

              {orphanedData.orphanedProducts.length > 0 && (
                <List dense sx={{ marginBottom: 2, maxHeight: 200, overflow: "auto" }}>
                  {orphanedData.orphanedProducts.slice(0, 10).map((product) => (
                    <ListItem key={product.id} divider>
                      <ListItemText
                        primary={`${product.sku} - ${product.name}`}
                        secondary={`Category: ${product.categoryRelation?.name || product.categoryId || 'N/A'} | Synced: ${product.synced_at ? new Date(product.synced_at).toLocaleDateString() : "Never"}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {orphanedData.orphanedProducts.length > 10 && (
                <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 2, display: "block" }}>
                  Showing first 10 of {orphanedData.orphanedCount} orphaned products
                </Typography>
              )}

              <Button
                variant="contained"
                color="warning"
                startIcon={
                  loading.deleteOrphanedProducts ? (
                    <CircularProgress size={20} />
                  ) : (
                    <DeleteIcon />
                  )
                }
                onClick={handleDeleteOrphanedProducts}
                disabled={loading.deleteOrphanedProducts}
                fullWidth
              >
                {loading.deleteOrphanedProducts
                  ? "Deleting Orphaned Products..."
                  : `Delete ${orphanedData.orphanedCount} Orphaned Products`}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Divider sx={{ marginY: 3 }} />

      {/* Danger Zone - Delete All Products */}
      <Card sx={{ marginBottom: 3, border: "2px solid #d32f2f" }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginBottom: 2,
            }}
          >
            <WarningIcon sx={{ marginRight: 1, color: "error.main" }} />
            <Typography variant="h6" color="error">
              DANGER ZONE
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            Delete ALL products from the entire database. This includes:
          </Typography>
          <ul style={{ marginTop: 0, color: "rgba(0, 0, 0, 0.6)" }}>
            <li>All product records</li>
            <li>All product_availability records (all stores)</li>
            <li>All product_attachment records (images)</li>
            <li>All product BOMs (Bill of Materials)</li>
          </ul>

          <Alert severity="error" sx={{ marginTop: 2, marginBottom: 2 }}>
            <Typography variant="body2">
              <strong>⚠️ THIS ACTION CANNOT BE UNDONE!</strong>
              <br />
              Only use this when completely resetting the dev database.
            </Typography>
          </Alert>

          <Button
            variant="contained"
            color="error"
            startIcon={
              loading.deleteAllProducts ? (
                <CircularProgress size={20} />
              ) : (
                <DeleteIcon />
              )
            }
            onClick={handleDeleteAllProducts}
            disabled={loading.deleteAllProducts}
            fullWidth
          >
            {loading.deleteAllProducts
              ? "Deleting All Products..."
              : "Delete ALL Products"}
          </Button>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({ open: false, title: "", message: "", action: () => {} })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            style={{ whiteSpace: "pre-line" }}
            paragraph
          >
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({
                open: false,
                title: "",
                message: "",
                action: () => {},
              })
            }
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDialog.action}
            color="error"
            variant="contained"
            autoFocus
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DevMenu;
